import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Stack, ViewMode } from "../../types";
import { useAppStore } from "../../store";
import { FunctionCard } from "../functions/FunctionCard";
import { STACK_COLORS } from "../../constants/colors";
import { DragHandle } from "../shared/DragHandle";

interface StackCardProps {
  stack: Stack;
  viewMode: ViewMode;
}

export function StackCard({ stack, viewMode }: StackCardProps) {
  const { addNewFunction, moveFunction } = useAppStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `stack:${stack.id}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleAddFunction = () => {
    addNewFunction(stack.id);
  };

  const handleFunctionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Parse IDs: func:stackId:funcId
    const activeParts = activeId.split(":");
    const overParts = overId.split(":");

    if (activeParts[0] !== "func" || overParts[0] !== "func") return;

    const fromStackId = activeParts[1];
    const funcId = activeParts[2];
    const toStackId = overParts[1];

    // Find the target index
    const toIndex = stack.functions.findIndex(
      (f) => `func:${toStackId}:${f.id}` === overId,
    );

    moveFunction(fromStackId, toStackId, funcId, toIndex >= 0 ? toIndex : 0);
  };

  // Filter functions based on view mode
  const filteredFunctions = stack.functions.filter((func) => {
    if (viewMode === "All") return true;
    return func.tools.some((tool) => tool.status === viewMode);
  });

  const functionIds = filteredFunctions.map(
    (func) => `func:${stack.id}:${func.id}`,
  );

  const headerColor =
    STACK_COLORS[stack.name as keyof typeof STACK_COLORS] || "#f5f5f5";

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={2}
      sx={{
        minWidth: 280,
        maxWidth: 320,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      {/* Stack Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: headerColor,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DragHandle
            listeners={listeners}
            attributes={attributes}
            size="medium"
          />
          <Typography variant="h6" fontWeight="bold">
            {stack.name}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleAddFunction}
          title="Add Function"
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Functions List */}
      <Box
        sx={{
          p: 1,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: "calc(100vh - 250px)",
          overflowY: "auto",
        }}
      >
        {filteredFunctions.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleFunctionDragEnd}
          >
            <SortableContext
              items={functionIds}
              strategy={verticalListSortingStrategy}
            >
              {filteredFunctions.map((func) => (
                <FunctionCard
                  key={func.id}
                  stackId={stack.id}
                  func={func}
                  viewMode={viewMode}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center" }}
          >
            No functions {viewMode !== "All" ? `with ${viewMode} tools` : ""}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
